export type BuiltSqlCommands = {
  grantPermissions: string;
  createOrDeleteUser?: string; // Only used for RDS
  grantOrRevokeAccess: string;
  manageRoleWithGrants: string;
};

export const buildSql = (
  serviceRole: string,
  installType: "cloud-sql" | "rds"
): BuiltSqlCommands => {
  const sql = {
    grantPermissions:
      installType === "rds"
        ? [
            grantPermissionsComment,
            rdsGrantPermissions,
            baseGrantPermissions,
          ].join("\n")
        : [grantPermissionsComment, baseGrantPermissions].join("\n"),
    createOrDeleteUser: installType === "rds" ? rdsCreateUser : undefined,
    grantOrRevokeAccess: grantOrRevokeAccessFunction,
    manageRoleWithGrants: manageRoleWithGrantsFunction,
  };

  const format = (str: string): string => {
    return str.replaceAll("${P0_SERVICE_ROLE}", serviceRole);
  };

  for (const key of Object.keys(sql) as (keyof typeof sql)[]) {
    const value = sql[key];
    if (value) {
      sql[key] = format(value);
    }
  }

  return sql;
};

const grantOrRevokeAccessFunction = `CREATE OR REPLACE FUNCTION p0GrantOrRevokeUserAccess(
  unsafe_user_name TEXT,
  unsafe_role_name TEXT,
  unsafe_action_type TEXT
)
  RETURNS void
  LANGUAGE plpgsql
AS
$$
DECLARE
  role_exists BOOLEAN;
  role_name TEXT := quote_ident(unsafe_role_name);
  user_name TEXT := quote_ident(unsafe_user_name);
  action_type TEXT := quote_literal(unsafe_action_type);
BEGIN
  -- Validate input
  IF unsafe_action_type NOT IN ('grant', 'revoke') THEN
    RAISE EXCEPTION 'Invalid action type: %', action_type;
  END IF;

  -- Prevent service account from granting itself access
  IF user_name ILIKE quote_ident('\${P0_SERVICE_ROLE}') THEN
    RAISE EXCEPTION 'Service account cannot grant or revoke access to itself';
  END IF;

  -- Check if role_name exists in the system
  SELECT EXISTS(SELECT 1 FROM pg_roles WHERE rolname = unsafe_role_name) INTO role_exists;

  IF NOT role_exists THEN
    RAISE EXCEPTION 'Role does not exist: %', role_name;
  END IF;

  -- Execute action
  IF unsafe_action_type = 'grant' THEN
    EXECUTE format('GRANT %s TO %s', role_name, user_name);
    RAISE NOTICE 'Granted % access to user %', role_name, user_name;
  ELSE
    EXECUTE format('REVOKE %s FROM %s', role_name, user_name);
    RAISE NOTICE 'Revoked % access from user %', role_name, user_name;
  END IF;
END;
$$ SECURITY DEFINER;

`;

const manageRoleWithGrantsFunction = `CREATE OR REPLACE FUNCTION p0ManageRoleWithGrants(
  unsafe_action_type text,
  unsafe_role_name text,
  unsafe_permission_type text DEFAULT NULL,
  unsafe_table_name text DEFAULT NULL,
  unsafe_schema_name text DEFAULT NULL
)
  RETURNS void
  LANGUAGE plpgsql
AS
$$
DECLARE
  role_exists boolean;
  action_type TEXT := quote_literal(unsafe_action_type);
  role_name TEXT := quote_ident(unsafe_role_name);
  permission_type TEXT := quote_ident(unsafe_permission_type);
  schema_name TEXT := quote_ident(unsafe_schema_name);
  table_name TEXT := quote_ident(unsafe_table_name);
BEGIN
  IF NOT unsafe_role_name ILIKE 'P0%' THEN
    RAISE EXCEPTION 'Invalid role specified';
  END IF;

  -- Check if the role exists
  SELECT EXISTS(SELECT 1 FROM pg_roles WHERE rolname = unsafe_role_name) INTO role_exists;

  IF unsafe_action_type = 'create' THEN
    -- Check if the role already exists
    IF NOT role_exists THEN
      -- Create the role if it does not exist
      EXECUTE format('CREATE ROLE %s', role_name);
    END IF;

    -- Grant USAGE on the schema
    IF schema_name IS NOT NULL THEN
      EXECUTE format('GRANT USAGE ON SCHEMA %s TO %s', schema_name, role_name);
    END IF;

    -- Grant permissions on the table or schema
    IF permission_type IS NOT NULL AND schema_name IS NOT NULL THEN
      -- For CREATE permission, grant CREATE on the schema (required to create tables)
      -- CREATE is a schema-level privilege, not a table-level privilege
      IF UPPER(unsafe_permission_type) = 'CREATE' THEN
        -- Grant CREATE permission on the schema (allows creating tables in that schema)
        EXECUTE format('GRANT CREATE ON SCHEMA %s TO %s', schema_name, role_name);
      ELSIF table_name IS NOT NULL THEN
        -- For other permission types, grant on the table as before
        EXECUTE format('GRANT %s ON %s.%s TO %s', LOWER(permission_type), schema_name, table_name, role_name);
      END IF;
    END IF;
  ELSIF unsafe_action_type = 'delete' THEN
    IF role_exists THEN
      -- Revoke specific privileges from the role on the table
      IF schema_name IS NOT NULL THEN
        EXECUTE format('REVOKE ALL PRIVILEGES ON ALL TABLES IN SCHEMA %s FROM %s', schema_name, role_name);
        -- Revoke all privileges on the schema
        EXECUTE format('REVOKE ALL PRIVILEGES ON SCHEMA %s FROM %s', schema_name, role_name);
      END IF;

      -- Revoke USAGE on the schema
      IF schema_name IS NOT NULL THEN
        EXECUTE format('REVOKE USAGE ON SCHEMA %s FROM %s', schema_name, role_name);
      END IF;

      -- Delete the role with case sensitivity
      BEGIN
        EXECUTE format('DROP ROLE IF EXISTS %s', role_name);
      EXCEPTION WHEN dependent_objects_still_exist THEN
        -- Do nothing, a subsequent call will remove remaining objects
      END;
    END IF;
  ELSE
    RAISE EXCEPTION 'Invalid action specified';
  END IF;
END;
$$ SECURITY DEFINER;

`;

const rdsCreateUser = `CREATE OR REPLACE FUNCTION p0CreateOrDeleteUser(
  unsafe_user_name TEXT,
  unsafe_action_type TEXT
)
  RETURNS void
  LANGUAGE plpgsql
AS
$$
DECLARE
  role_exists boolean;
  user_name TEXT := quote_ident(unsafe_user_name);
  action_type TEXT := quote_literal(unsafe_action_type);
BEGIN
  -- Check if the role exists
  SELECT EXISTS(SELECT 1 FROM pg_roles WHERE rolname = unsafe_user_name) INTO role_exists;
  IF unsafe_action_type = 'create' THEN
    EXECUTE format('CREATE USER %s WITH LOGIN', user_name);
    EXECUTE format('GRANT rds_iam TO %s', user_name);
  ELSIF unsafe_action_type = 'delete' THEN
    IF role_exists THEN
      EXECUTE format('DROP USER IF EXISTS %s', user_name);
    END IF;
  ELSE
    RAISE EXCEPTION 'Invalid action specified';
  END IF;
END;
$$ SECURITY DEFINER;

`;

const grantPermissionsComment = `-- Provide the service account with execute permissions for the two newly created functions.`;

const baseGrantPermissions = `GRANT EXECUTE ON FUNCTION p0GrantOrRevokeUserAccess(user_name TEXT, role_name TEXT, action_type TEXT)
TO "\${P0_SERVICE_ROLE}";

GRANT EXECUTE ON FUNCTION p0ManageRoleWithGrants(action_type TEXT, role_name TEXT, permission_type TEXT, table_name TEXT, schema_name TEXT)
TO "\${P0_SERVICE_ROLE}";`;

const rdsGrantPermissions = `CREATE USER \${P0_SERVICE_ROLE} WITH LOGIN;
GRANT rds_iam TO \${P0_SERVICE_ROLE};

GRANT EXECUTE ON FUNCTION p0CreateOrDeleteUser(user_name TEXT, action_type TEXT)
TO "\${P0_SERVICE_ROLE}";`;
